import { store } from '@redux/store';
import { filter, switchMap, take, takeWhile, timer } from 'rxjs';
import ModalStep from '../enums/modalStep';
import verifyUserIdentification$ from '../apis/verifyUserIdentification';
import { setModalStep } from '../redux/slices';
import { getStoredUserData } from '../services/userService';

export default class VerificationInProgress {
  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.class = 'nhs-c-self-guided-left-panel';
    this.containerSelector = 'data-verification-in-progress';
    this.nextStep = ModalStep.Questions;
    this.previousStep = ModalStep.IdentityVerification;
    this.pollUserVerificationSubscriber = null;
    this.pollingFrequency = 5000;
    this.maxPollAttempts = 20;
    this.userInfo = {};
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   *
   */
  handleSecondaryAction() {
    VerificationInProgress.setModalStep(this.previousStep);
  }

  /**
   * Handles the DOM update event and sets up events if the current step is IframeVerification.
   *
   * @param {CustomEvent} event - The DOM update event containing details about the current step.
   * @param {Object} event.detail - The detail object of the event.
   * @param {string} event.detail.step - The current step in the modal process.
   */
  onDomUpdate(event) {
    if (event.detail && event.detail.step === ModalStep.VerificationInProgress) {
      this.pollUserVerificationSubscriber = this.pollUserVerification$().subscribe({
        next: this.setUserInfo.bind(this),
        complete: this.onUserVerified.bind(this),
      });
      return;
    }
    this.pollUserVerificationSubscriber?.unsubscribe();
  }

  pollUserVerification$() {
    return timer(0, this.pollingFrequency).pipe(
      take(this.maxPollAttempts),
      switchMap(() => this.verifyUserIdentification$()),
      takeWhile(({ verified, errorMessage }) => !verified && errorMessage === null, true),
      filter(VerificationInProgress.isVerificationCompleted)
    );
  }

  /**
   * Verifies the user's identification by utilizing the verification information
   * and specification data from the root component. Subscribes to the verification
   * process and triggers the user verification handler upon completion.
   */
  verifyUserIdentification$() {
    const { identificationVerificationId } = this.rootComponent.verificationInfo;
    const { specId } = this.rootComponent.specData;
    return verifyUserIdentification$({ verificationId: identificationVerificationId, specId });
  }

  /**
   * Handles the user verification process and updates the application state accordingly.
   *
   * @param {Object} data - The data object containing user verification details.
   * @param {boolean} data.verified - Indicates whether the user has been successfully verified.
   * @param {string} [data.firstName] - The first name of the user (provided if verified).
   * @param {string} [data.lastName] - The last name of the user (provided if verified).
   *
   * If the user is verified, their information is updated in the application state, and the modal
   * step is advanced to the "Questions" step. If verification fails, an error state is presented,
   * and the modal step is reset to "IdentityVerification."
   */
  onUserVerified() {
    const { verified, firstName, lastName } = this.userInfo;
    const userData = { ...getStoredUserData() };
    if (verified) {
      VerificationInProgress.setModalStep(ModalStep.Questions);
      userData.firstName = firstName;
      userData.lastName = lastName;
      this.rootComponent.onUserChange$.next(userData);
      return;
    }

    this.rootComponent.presentErrorState$.next(true);
    VerificationInProgress.setModalStep(ModalStep.IdentityVerification);
  }

  setUserInfo(userInfo) {
    this.userInfo = userInfo;
    return this;
  }

  /**
   * Renders the verification component for the self-guided tour.
   *
   * @returns {string} The HTML string for the verification component, including a heading
   * and an iframe for identification verification.
   */
  render() {
    return `
      <div class="${this.class}">
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Verification in progress</h1>
          <p class="nhs-c-label--black-900-body-1-medium-regular">Please wait until verification completes. This may take a couple of minutes.</p>
        </div>
      </div>
    `;
  }

  /**
   * Updates the error state in the root component.
   */
  presentErrorState() {
    this.rootComponent.presentErrorState$.next(true);
  }

  /**
   * Dispatches an action to update the modal step in the application's state.
   *
   * @param {ModalStep} step - The modal step to set in the application's state.
   */
  static setModalStep(step) {
    store.dispatch(setModalStep({ modalStep: step }));
  }

  static isVerificationCompleted({ verified, errorMessage }) {
    return verified || !!errorMessage;
  }
}
